import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile, updateMyProfile } from "@/lib/account/account.functions";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { scorePassword, STRENGTH_COLORS, STRENGTH_LABELS } from "@/lib/account/password-strength";
import { ShieldCheck, Eye, EyeOff, Check, X } from "lucide-react";

export const Route = createFileRoute("/_account/account/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const qc = useQueryClient();
  const get = useServerFn(getMyProfile);
  const upd = useServerFn(updateMyProfile);
  const profQ = useQuery({ queryKey: ["account", "profile"], queryFn: () => get() });

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const p = profQ.data?.profile;
    if (p) {
      setFirst(p.first_name ?? "");
      setLast(p.last_name ?? "");
      setPhone(p.phone ?? "");
      setMarketing(!!p.marketing_opt_in);
    }
  }, [profQ.data]);

  const save = useMutation({
    mutationFn: () =>
      upd({
        data: {
          first_name: first || null,
          last_name: last || null,
          phone: phone || null,
          marketing_opt_in: marketing,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "profile"] });
      toast.success("Profile updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Profile</h1>
        <p className="text-sm text-slate-500">Manage your personal information.</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Personal</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="mt-4 space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="f">First name</Label>
              <Input id="f" value={first} onChange={(e) => setFirst(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="l">Last name</Label>
              <Input id="l" value={last} onChange={(e) => setLast(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={profQ.data?.email ?? ""} disabled className="mt-1 bg-slate-50" />
            <p className="mt-1 text-xs text-slate-500">Email changes are handled through password reset.</p>
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#2563EB]"
            />
            <span>Send me product news and discounts. Unsubscribe anytime.</span>
          </label>
          <Button type="submit" disabled={save.isPending} className="bg-[#2563EB] hover:bg-[#1d4ed8]">
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </form>
      </section>

      <ChangePasswordCard />
      <SessionsCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);
  type Strength = Awaited<ReturnType<typeof scorePassword>>;
  const [strength, setStrength] = useState<Strength>({
    score: 0,
    label: STRENGTH_LABELS[0],
    color: STRENGTH_COLORS[0],
    warning: "",
    suggestions: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await scorePassword(pwd);
      if (!cancelled) setStrength(r);
    })();
    return () => { cancelled = true; };
  }, [pwd]);

  const requirements = useMemo(() => ([
    { ok: pwd.length >= 10, label: "At least 10 characters" },
    { ok: /[A-Z]/.test(pwd) && /[a-z]/.test(pwd), label: "Upper and lower case letters" },
    { ok: /\d/.test(pwd), label: "At least one number" },
    { ok: /[^\w\s]/.test(pwd), label: "At least one symbol" },
  ]), [pwd]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!current) return toast.error("Enter your current password");
    if (pwd.length < 10) return toast.error("Password must be at least 10 characters");
    if (pwd !== confirm) return toast.error("Passwords do not match");
    if (strength.score < 3) return toast.error("Password is too weak — try adding words or symbols");
    setBusy(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const email = u.user?.email;
      if (!email) throw new Error("Session expired");
      // Re-authenticate to prevent session-hijack password reset.
      const rea = await supabase.auth.signInWithPassword({ email, password: current });
      if (rea.error) throw new Error("Current password is incorrect");
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      toast.success("Password updated");
      setCurrent(""); setPwd(""); setConfirm("");
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally { setBusy(false); }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[#2563EB]" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Change password</h2>
      </div>
      <form onSubmit={submit} className="mt-4 space-y-3 max-w-md">
        <div>
          <Label htmlFor="cur">Current password</Label>
          <Input id="cur" type={reveal ? "text" : "password"} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1" required />
        </div>
        <div>
          <Label htmlFor="np">New password</Label>
          <div className="relative mt-1">
            <Input id="np" type={reveal ? "text" : "password"} autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="pr-10" />
            <button type="button" onClick={() => setReveal((r) => !r)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Toggle password visibility">
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {pwd && (
            <>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full transition-all"
                  style={{ width: `${((strength.score + 1) / 5) * 100}%`, background: strength.color }}
                />
              </div>
              <p className="mt-1 text-xs font-medium" style={{ color: strength.color }}>{strength.label}</p>
              {strength.warning && <p className="text-xs text-amber-700">{strength.warning}</p>}
            </>
          )}
          <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            {requirements.map((r) => (
              <li key={r.label} className={`flex items-center gap-1 ${r.ok ? "text-emerald-700" : "text-slate-500"}`}>
                {r.ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {r.label}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Label htmlFor="cp">Confirm new password</Label>
          <Input id="cp" type={reveal ? "text" : "password"} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
          {confirm && confirm !== pwd && <p className="mt-1 text-xs text-rose-600">Passwords do not match</p>}
        </div>
        <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </section>
  );
}

function SessionsCard() {
  const [busy, setBusy] = useState(false);
  async function signOutOthers() {
    setBusy(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: "others" as any });
      if (error) throw error;
      toast.success("Signed out on other devices");
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally { setBusy(false); }
  }
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Sessions</h2>
      <p className="mt-2 text-sm text-slate-600">Lost a phone or laptop? Revoke all other sessions immediately.</p>
      <Button onClick={signOutOthers} disabled={busy} variant="outline" className="mt-3">
        {busy ? "Working…" : "Sign out on other devices"}
      </Button>
    </section>
  );
}